import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { ArrowRight, FileText, Shield, Network, CheckCircle, Play, X, Mail, Phone, Cpu, Zap, Target } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent } from '../components/ui/dialog';
import { capabilities, documents } from '../data/mockData';
import HeroNetworkCanvas from '../components/HeroNetworkCanvas';


const AWS_MARKETPLACE_PUBLIC_URL = '';
const AWS_MARKETPLACE_PRIVATE_OFFER_PATH = '/contact?subject=AWS%20Marketplace%20Private%20Offer%20Request';

const Home = () => {
  const [showVideo, setShowVideo] = useState(false);
  const awsMarketplaceCtaLabel = AWS_MARKETPLACE_PUBLIC_URL
    ? 'View AWS Marketplace Listing'
    : 'Request AWS Marketplace Private Offer';
  const awsMarketplaceCtaHref = AWS_MARKETPLACE_PUBLIC_URL || AWS_MARKETPLACE_PRIVATE_OFFER_PATH;

  return (
    <div className="min-h-screen">
      <Helmet>
        <title>Semiconductor | Electronics | Reliability | AI Infrastructure | SALAR Technologies</title>
        <meta name="description" content="SALAR Technologies LLC provides defense-electronics and semiconductor supply-chain risk intelligence, provenance and counterfeit-risk decision support, board reliability engineering, AI-fabric assurance, and AI-infrastructure power/thermal reliability." />
        <meta property="og:title" content="Semiconductor | Electronics | Reliability | AI Infrastructure | SALAR Technologies" />
        <meta property="og:description" content="Engineering-first defense electronics and semiconductor assurance: lower-tier dependency mapping, provenance evidence, mission-critical reliability, AI-fabric validation, and AI-infrastructure resilience." />
        <meta property="og:url" content="https://www.salartechnologies.com/" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://customer-assets.emergentagent.com/job_salar-web-app/artifacts/j5g8tzgj_image.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Semiconductor | Electronics | Reliability | AI Infrastructure | SALAR Technologies" />
        <meta name="twitter:description" content="Engineering-first defense electronics and semiconductor assurance: lower-tier dependency mapping, provenance evidence, mission-critical reliability, AI-fabric validation, and AI-infrastructure resilience." />
        <meta name="twitter:image" content="https://customer-assets.emergentagent.com/job_salar-web-app/artifacts/j5g8tzgj_image.png" />
        <link rel="canonical" href="https://www.salartechnologies.com/" />
        <meta name="robots" content="index, follow" />
      </Helmet>
      {/* Hero Section — custom <canvas> network animation, investor-facing copy */}
      <section
        className="relative text-white py-32 lg:py-40 overflow-hidden"
        style={{ background: '#060A14' }}
      >
        {/* Layer 0: custom hand-written canvas network (z-index:0, contained in hero only) */}
        <HeroNetworkCanvas />

        {/* Layer 0.5: soft dark gradient overlay so hero text stays fully readable */}
        <div
          className="absolute inset-0 pointer-events-none"
          aria-hidden="true"
          style={{
            zIndex: 0,
            background:
              'linear-gradient(180deg, rgba(6,10,20,0.45) 0%, rgba(6,10,20,0.35) 50%, rgba(6,10,20,0.55) 100%)',
          }}
        ></div>

        <div className="container mx-auto px-6 relative" style={{ zIndex: 1 }}>
          <div className="max-w-5xl mx-auto">
            <div className="text-center">
              <div className="inline-flex items-center space-x-2 px-4 py-2 bg-blue-600/30 rounded-full text-sm font-medium border border-cyan-400/40 backdrop-blur-sm mb-8">
                <Cpu size={16} className="text-cyan-400" />
                <span className="font-semibold">Hardware Innovation • Trusted Electronics • Advanced Systems</span>
              </div>

              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
                <span className="text-white drop-shadow-2xl">
                  <span className="text-cyan-300">Semiconductor</span> · <span className="text-blue-200">Electronics</span> · <span className="text-cyan-200">Reliability</span> · <span className="text-blue-200">AI Infrastructure</span>
                </span>
              </h1>

              <p
                className="text-lg md:text-xl lg:text-2xl text-blue-50 mb-5 leading-relaxed max-w-4xl mx-auto font-light"
                data-testid="home-hero-subhead"
              >
                U.S. engineering and R&D for semiconductor systems, electronics reliability, supply-chain resilience, provenance, defense electronics, AI infrastructure, manufacturing stability, and decision-grade technical evidence.
              </p>

              <p
                className="text-base md:text-lg text-cyan-300 font-semibold mb-10 max-w-3xl mx-auto"
                data-testid="home-hero-credibility"
              >
                Ten patent filings · defense, commercial, industrial, AI infrastructure, and consumer electronics scope · Oregon-based U.S. LLC
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/licensing" data-testid="home-hero-licensing-cta">
                  <Button size="lg" className="bg-cyan-500 hover:bg-cyan-400 text-[#0a1628] font-bold text-lg px-8 py-6 shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all">
                    <FileText className="mr-2" size={24} />
                    Patent Licensing
                  </Button>
                </Link>
                <a
                  href="https://customer-assets.emergentagent.com/job_salar-web-app/artifacts/i1hsfwx8_SALAR_Government_Capability_Statement.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button size="lg" className="bg-blue-600 text-white hover:bg-blue-700 font-semibold text-lg px-8 py-6 shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all">
                    <Target className="mr-2" size={24} />
                    Explore Capabilities
                  </Button>
                </a>
                <Button
                  size="lg"
                  variant="outline"
                  className="border-2 border-white text-white hover:bg-white hover:text-blue-900 font-semibold text-lg px-8 py-6 shadow-xl backdrop-blur-sm"
                  onClick={() => setShowVideo(true)}
                >
                  <Play className="mr-2" size={24} />
                  Watch Introduction
                </Button>
                <Link to="/government-defense">
                  <Button size="lg" variant="outline" className="border-2 border-cyan-400 text-cyan-100 hover:bg-cyan-400 hover:text-blue-900 font-semibold text-lg px-8 py-6 shadow-xl backdrop-blur-sm">
                    <Shield className="mr-2" size={24} />
                    Government Access
                  </Button>
                </Link>
                <a href="/category-authority.html">
                  <Button size="lg" variant="outline" className="border-2 border-blue-300 text-blue-100 hover:bg-blue-200 hover:text-blue-950 font-semibold text-lg px-8 py-6 shadow-xl backdrop-blur-sm">
                    <Network className="mr-2" size={24} />
                    Technical Knowledge Library
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Video Dialog */}
        <Dialog open={showVideo} onOpenChange={setShowVideo}>
          <DialogContent className="max-w-5xl p-0 bg-transparent border-0">
            <div className="relative bg-black rounded-lg overflow-hidden">
              <button
                onClick={() => setShowVideo(false)}
                className="absolute top-4 right-4 z-50 p-2 bg-black/50 hover:bg-black/70 rounded-full text-white transition-colors"
              >
                <X size={24} />
              </button>
              <div className="aspect-video">
                <video
                  className="w-full h-full"
                  controls
                  autoPlay
                  src="https://customer-assets.emergentagent.com/job_salar-web-app/artifacts/sz1dqbdi_SALAR_Technologies_Intro_Video_Professional_Music_WebReady%20%282%29.MP4"
                >
                  Your browser does not support the video tag.
                </video>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </section>


      <section className="py-12 bg-[#08111f] border-y border-cyan-500/15" aria-label="Short technical topics">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto text-center">
            <p className="text-xs font-bold uppercase tracking-[0.22em] text-cyan-300 mb-4">Short technical topics</p>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">Semiconductor · Hardware Engineering · PCIe · Ethernet · Reliability · AI Infrastructure</h2>
            <div className="flex flex-wrap justify-center gap-3">
              {[
                ['Semiconductor','/semiconductor.html'],
                ['Electronics','/electronics.html'],
                ['Reliability','/reliability.html'],
                ['Supply Chain','/supply-chain.html'],
                ['Provenance','/provenance.html'],
                ['AI Infrastructure','/ai-infrastructure.html'],
                ['Defense Electronics','/defense-electronics.html'],
                ['Hardware Engineering','/hardware-engineering.html'],
                ['PCIe','/pcie.html'],
                ['Ethernet','/ethernet.html'],
                ['NIC','/nic.html'],
                ['FPGA','/fpga.html'],
                ['SerDes','/serdes.html'],
                ['Board Design','/board-design.html'],
                ['Validation','/validation.html'],
                ['Qualification','/qualification.html'],
                ['NPI','/npi.html'],
                ['Thermal','/thermal.html'],
                ['AI Orchestration','/ai-orchestration.html'],
                ['Thermal Orchestration','/thermal-orchestration.html'],
                ['Failure Analysis','/failure-analysis.html'],
                ['Manufacturing','/manufacturing.html'],
                ['DMSMS','/dmsms.html'],
              ].map(([label, href]) => (
                <a key={href} href={href} className="rounded-full border border-cyan-400/30 bg-cyan-400/10 px-4 py-2 text-sm font-bold text-cyan-100 hover:bg-cyan-400/20">
                  {label}
                </a>
              ))}
            </div>
          </div>
        </div>
      </section>
      {/* Procurement-Ready Assurance Services — AWS Marketplace flagship */}
      <section
        className="py-16 bg-gradient-to-b from-[#060A14] to-[#0a1628] border-b border-cyan-500/20"
        data-testid="procurement-ready-services-section"
      >
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-10">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold tracking-wider uppercase border border-cyan-400/40 bg-cyan-500/10 text-cyan-200 mb-4">
                <span className="w-1.5 h-1.5 rounded-full bg-cyan-400"></span>
                Procurement-Ready Assurance Services
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-3">
                Now available through AWS Marketplace
              </h2>
              <p className="text-base text-blue-200 max-w-3xl mx-auto leading-relaxed">
                Scoped, private-offer professional-services engagements for thermal, power, telemetry, supplier/spares, failover, and continuity-risk assurance across cloud, colocation, enterprise, data-center, and AI/GPU compute environments.
              </p>
            </div>

            <Card
              className="border-cyan-400/40 bg-gradient-to-br from-blue-900/50 to-indigo-950/40 ring-1 ring-cyan-400/20 hover:ring-cyan-400/50 transition-all"
              data-testid="aws-marketplace-card"
            >
              <CardContent className="p-7 md:p-9">
                <div className="flex flex-wrap items-center gap-2 mb-4">
                  <Badge variant="outline" className="border-cyan-400 text-cyan-200 font-mono uppercase tracking-wider">
                    Flagship Service
                  </Badge>
                  <Badge variant="outline" className="border-emerald-400/50 text-emerald-200 font-mono">
                    Professional Services · Private Offer
                  </Badge>
                </div>
                <h3 className="text-2xl md:text-3xl font-bold text-white mb-3">
                  Data Center Thermal Resilience Assurance
                </h3>
                <p className="text-base text-blue-100 leading-relaxed mb-6">
                  Power, cooling, telemetry, failover, supplier/spares, operations, and continuity-risk assessment for AWS-dependent, cloud, colocation, enterprise, and AI compute environments. SALAR converts fragmented engineering evidence into a <strong className="text-white">P1–P5 resilience scorecard</strong>, gap heat map, ranked risk drivers, decision trace, and prioritized mitigation backlog.
                </p>

                {/* P1–P5 quick legend */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-6">
                  {[
                    { tag: 'P1', label: 'Thermal / Power Design' },
                    { tag: 'P2', label: 'Telemetry & Evidence' },
                    { tag: 'P3', label: 'Supplier / Provenance' },
                    { tag: 'P4', label: 'Operations & Stability' },
                    { tag: 'P5', label: 'Continuity & Qualification' },
                  ].map((p) => (
                    <div
                      key={p.tag}
                      className="rounded border border-cyan-400/20 bg-cyan-500/5 px-3 py-2"
                    >
                      <div className="text-xs font-mono font-bold text-cyan-300">{p.tag}</div>
                      <div className="text-[11px] text-blue-200 leading-tight mt-0.5">{p.label}</div>
                    </div>
                  ))}
                </div>

                <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
                  {AWS_MARKETPLACE_PUBLIC_URL ? (
                    <a
                      href={awsMarketplaceCtaHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="aws-marketplace-cta"
                    >
                      <Button
                        size="lg"
                        className="bg-cyan-500 hover:bg-cyan-400 text-[#0a1628] font-bold text-base px-7 py-5 shadow-xl hover:shadow-2xl transition-all"
                      >
                        {awsMarketplaceCtaLabel}
                        <ArrowRight className="ml-2" size={18} />
                      </Button>
                    </a>
                  ) : (
                    <Link to={awsMarketplaceCtaHref} data-testid="aws-marketplace-cta">
                      <Button
                        size="lg"
                        className="bg-cyan-500 hover:bg-cyan-400 text-[#0a1628] font-bold text-base px-7 py-5 shadow-xl hover:shadow-2xl transition-all"
                      >
                        {awsMarketplaceCtaLabel}
                        <ArrowRight className="ml-2" size={18} />
                      </Button>
                    </Link>
                  )}
                  <Link to="/contact" data-testid="aws-marketplace-contact-cta">
                    <Button
                      size="lg"
                      variant="outline"
                      className="border-2 border-cyan-400 text-cyan-100 hover:bg-cyan-400 hover:text-blue-900 font-semibold text-base px-7 py-5"
                    >
                      Scope an Engagement
                    </Button>
                  </Link>
                </div>

                <p className="text-xs text-blue-200/80 mt-4 leading-relaxed">
                  SALAR's AWS Marketplace engagement is handled through scoped professional-services/private-offer routing. If the public AWS listing link is not available for your account, please request a private-offer discussion.
                </p>

                <p className="text-xs text-blue-300/70 italic mt-3 leading-relaxed">
                  No Amazon, AWS, or cloud-provider endorsement is implied. This service is aligned with SALAR's patent-pending work in high-density AI infrastructure power and thermal orchestration.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
      <section className="py-20 bg-gradient-to-b from-blue-950/30 to-transparent">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-4xl font-bold text-white mb-4 text-center">Core Capabilities</h2>
            <p className="text-lg text-blue-200 text-center mb-10 max-w-3xl mx-auto">
              Engineering-driven solutions delivering measurable risk reduction and supply chain intelligence
            </p>

            {/* Capabilities visual — abstract four-lane quad chart */}
            <div className="max-w-2xl mx-auto mb-14">
              <div
                className="rounded-xl overflow-hidden border border-cyan-400/25"
                style={{ boxShadow: '0 8px 32px rgba(0,0,0,0.35)' }}
              >
                <img
                  src="/img/salar_quadchart_fourlane.webp"
                  alt="Abstract four-lane technical visual representing electronics reliability, provenance control, supply-chain intelligence, and manufacturing stability."
                  loading="lazy"
                  className="block w-full h-auto"
                  data-testid="home-capabilities-quadchart"
                />
              </div>
              <p className="text-xs text-blue-300/80 text-center mt-3 italic">
                Four patent-aligned lanes: reliability, provenance, supply-chain intelligence, manufacturing stability.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {/* Pillar 1 - Manufacturing Stability themed */}
              <Card
                className="border-blue-500/30 backdrop-blur-sm hover:scale-105 transition-transform duration-300 overflow-hidden"
                style={{
                  backgroundImage: "linear-gradient(rgba(10,22,40,0.6), rgba(10,22,40,0.85)), url('/img/pexels_server_room.jpg')",
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                }}
                data-testid="home-pillar-provenance"
              >
                <CardHeader>
                  <CardTitle className="text-2xl text-cyan-300">Provenance Confidence &amp; Source-Risk Intelligence</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-blue-100 leading-relaxed">
                    Evidence-based decision support for sourcing, qualification, sustainment, and grey-market risk
                    review. SALAR helps teams identify accumulated source-risk indicators before release decisions are
                    made — without replacing lab testing or expert counterfeit determinations. Output: an auditable,
                    explainable decision memo showing what evidence drove the recommendation.
                  </p>
                </CardContent>
              </Card>

              {/* Pillar 2 - Supply Chain Intelligence themed */}
              <Card
                className="border-blue-500/30 backdrop-blur-sm hover:scale-105 transition-transform duration-300 overflow-hidden"
                style={{
                  backgroundImage: "linear-gradient(rgba(10,22,40,0.6), rgba(10,22,40,0.85)), url('/img/pexels_fiber_cables.jpg')",
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                }}
                data-testid="home-pillar-supply"
              >
                <CardHeader>
                  <CardTitle className="text-2xl text-cyan-300">Supply Chain Concentration Analysis</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-blue-100 leading-relaxed">
                    We quantify single-source and geographic dependency exposure across your critical BOM — rare earth inputs, advanced node ICs, and sole-source assemblies. Output: a prioritized concentration map with dual-source recommendations.
                  </p>
                </CardContent>
              </Card>

              {/* Pillar 3 - Reliability Engineering themed */}
              <Card
                className="border-blue-500/30 backdrop-blur-sm hover:scale-105 transition-transform duration-300 overflow-hidden"
                style={{
                  backgroundImage: "linear-gradient(rgba(10,22,40,0.6), rgba(10,22,40,0.85)), url('/img/circuit_chip.jpg')",
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                }}
                data-testid="home-pillar-reliability"
              >
                <CardHeader>
                  <CardTitle className="text-2xl text-cyan-300">Board-Level Reliability Engineering</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-blue-100 leading-relaxed">
                    We identify failure risk at the component, solder joint, and assembly level using proven reliability models — reducing field escapes and costly redesign cycles. Backed by ten patent filings spanning reliability, connectivity, supply-chain intelligence, manufacturing stability, AI infrastructure, provenance, agentic governance, high-speed validation, and quantum hardware assurance.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Hardware Innovation Showcase - NEW SECTION */}
      <section className="py-20 bg-gradient-to-b from-blue-900/40 via-blue-950/60 to-blue-900/40 border-y border-cyan-500/30 relative overflow-hidden">
        {/* Floating R&D Images with 3D Animation */}
        <div className="absolute top-20 right-12 z-0 opacity-50" style={{ animation: 'float3d 11s ease-in-out infinite', animationDelay: '1s' }}>
          <img 
            src="https://images.unsplash.com/photo-1758206523826-a65d4cf070aa?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwxfHxyZXNlYXJjaCUyMGRldmVsb3BtZW50JTIwbGFib3JhdG9yeSUyMHRlY2hub2xvZ3klMjBzY2llbnRpc3RzfGVufDB8fHx8MTc3NjM3Mjg1MHww&ixlib=rb-4.1.0&q=85"
            alt="Research Laboratory"
            className="w-64 h-64 rounded-2xl border-4 border-blue-400/30 shadow-2xl object-cover"
            style={{ transform: 'perspective(1000px) rotateY(-25deg) rotateX(10deg)' }}
          />
        </div>
        
        <div className="absolute bottom-32 left-16 z-0 opacity-40" style={{ animation: 'float3d 13s ease-in-out infinite', animationDelay: '3s' }}>
          <img 
            src="https://images.unsplash.com/photo-1536905379701-c8aba0076deb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwyfHxlbGVjdHJvbmljcyUyMGVuZ2luZWVyaW5nJTIwcmVzZWFyY2glMjBsYWIlMjBlcXVpcG1lbnQlMjB0ZXN0aW5nfGVufDB8fHx8MTc3NjM3Mjg1MHww&ixlib=rb-4.1.0&q=85"
            alt="Testing Equipment"
            className="w-56 h-56 rounded-2xl border-4 border-cyan-400/30 shadow-2xl object-cover"
            style={{ transform: 'perspective(1000px) rotateY(20deg) rotateX(-15deg)' }}
          />
        </div>
        
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-7xl mx-auto">
            {/* Section Header */}
            <div className="text-center mb-16">
              <div className="inline-flex items-center space-x-2 px-6 py-3 bg-cyan-600/30 rounded-full text-sm font-bold border-2 border-cyan-400/50 backdrop-blur-md mb-6">
                <Cpu size={20} className="text-cyan-300" />
                <span className="text-cyan-100">HARDWARE INNOVATION EXCELLENCE</span>
              </div>
              <h2 className="text-5xl font-bold text-white mb-6">
                Advanced <span className="text-cyan-300">Microelectronics</span> & <span className="text-blue-200">Hardware Engineering</span>
              </h2>
              <p className="text-xl text-blue-100 max-w-4xl mx-auto leading-relaxed">
                Precision-engineered solutions for semiconductor assurance, board-level reliability, 
                and trusted electronics in defense-critical applications
              </p>
            </div>

            {/* Hardware Innovation Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
              {/* Circuit Board Engineering */}
              <Card className="bg-gradient-to-br from-blue-900/60 to-cyan-900/40 border-2 border-cyan-400/40 backdrop-blur-md hover:scale-105 transform transition-all duration-300 shadow-2xl hover:shadow-cyan-500/50">
                <CardHeader>
                  <div className="w-full h-48 rounded-lg overflow-hidden mb-4 border-2 border-cyan-400/30">
                    <img 
                      src="https://images.unsplash.com/photo-1760842543741-876d7837fa0b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHw0fHxjaXJjdWl0JTIwYm9hcmQlMjBtaWNyb2NoaXAlMjBoYXJkd2FyZSUyMGVuZ2luZWVyaW5nfGVufDB8fHx8MTc3NjM3MDIxMnww&ixlib=rb-4.1.0&q=85"
                      alt="Advanced Circuit Board Engineering"
                      className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <CardTitle className="text-2xl text-white flex items-center">
                    <Cpu className="mr-2 text-cyan-400" size={28} />
                    Circuit Board Architecture
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-blue-100 space-y-3">
                  <p className="leading-relaxed">
                    Board-level reliability analysis, trace routing optimization, thermal management, 
                    and signal integrity validation for mission-critical systems.
                  </p>
                  <div className="flex flex-wrap gap-2 pt-2">
                    <Badge className="bg-cyan-500/30 text-cyan-100 border border-cyan-400/50">PCB Design</Badge>
                    <Badge className="bg-blue-500/30 text-blue-100 border border-blue-400/50">Reliability</Badge>
                    <Badge className="bg-cyan-500/30 text-cyan-100 border border-cyan-400/50">Testing</Badge>
                  </div>
                </CardContent>
              </Card>

              {/* Semiconductor Manufacturing */}
              <Card className="bg-gradient-to-br from-cyan-900/60 to-blue-900/40 border-2 border-blue-400/40 backdrop-blur-md hover:scale-105 transform transition-all duration-300 shadow-2xl hover:shadow-blue-500/50">
                <CardHeader>
                  <div className="w-full h-48 rounded-lg overflow-hidden mb-4 border-2 border-blue-400/30">
                    <img 
                      src="https://images.unsplash.com/photo-1669101283516-e608dcf142df?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHwxfHxzZW1pY29uZHVjdG9yJTIwbWFudWZhY3R1cmluZyUyMGNsZWFuJTIwcm9vbSUyMHRlY2hub2xvZ3l8ZW58MHx8fHwxNzc2MzcwMjEyfDA&ixlib=rb-4.1.0&q=85"
                      alt="Semiconductor Manufacturing Excellence"
                      className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <CardTitle className="text-2xl text-white flex items-center">
                    <Zap className="mr-2 text-blue-400" size={28} />
                    Semiconductor Assurance
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-blue-100 space-y-3">
                  <p className="leading-relaxed">
                    Manufacturing process stability, cleanroom protocol validation, die-level inspection, 
                    and provenance verification for trusted microelectronics.
                  </p>
                  <div className="flex flex-wrap gap-2 pt-2">
                    <Badge className="bg-blue-500/30 text-blue-100 border border-blue-400/50">Cleanroom</Badge>
                    <Badge className="bg-cyan-500/30 text-cyan-100 border border-cyan-400/50">Quality Control</Badge>
                    <Badge className="bg-blue-500/30 text-blue-100 border border-blue-400/50">Assurance</Badge>
                  </div>
                </CardContent>
              </Card>

              {/* Microchip Analysis */}
              <Card className="bg-gradient-to-br from-blue-900/60 to-cyan-900/40 border-2 border-cyan-400/40 backdrop-blur-md hover:scale-105 transform transition-all duration-300 shadow-2xl hover:shadow-cyan-500/50">
                <CardHeader>
                  <div className="w-full h-48 rounded-lg overflow-hidden mb-4 border-2 border-cyan-400/30">
                    <img 
                      src="https://images.unsplash.com/photo-1771189957041-4c1ed745a9af?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwzfHxjaXJjdWl0JTIwYm9hcmQlMjBtaWNyb2NoaXAlMjBoYXJkd2FyZSUyMGVuZ2luZWVyaW5nfGVufDB8fHx8MTc3NjM3MDIxMnww&ixlib=rb-4.1.0&q=85"
                      alt="Microchip Component Analysis"
                      className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <CardTitle className="text-2xl text-white flex items-center">
                    <Target className="mr-2 text-cyan-400" size={28} />
                    Component Intelligence
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-blue-100 space-y-3">
                  <p className="leading-relaxed">
                    Provenance-confidence scoring, supply-chain exposure analysis, component-level evidence governance,
                    and lifecycle sustainment decision support for defense electronics.
                  </p>
                  <div className="flex flex-wrap gap-2 pt-2">
                    <Badge className="bg-cyan-500/30 text-cyan-100 border border-cyan-400/50">Evidence</Badge>
                    <Badge className="bg-blue-500/30 text-blue-100 border border-blue-400/50">Provenance</Badge>
                    <Badge className="bg-cyan-500/30 text-cyan-100 border border-cyan-400/50">Decision Support</Badge>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Hardware Innovation Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 bg-gradient-to-r from-cyan-900/40 via-blue-900/50 to-cyan-900/40 backdrop-blur-md rounded-2xl p-8 border-2 border-cyan-400/30">
              <div className="text-center">
                <div className="text-5xl font-bold text-cyan-300 mb-2">100%</div>
                <div className="text-sm text-blue-100 font-semibold">U.S.-Based Engineering</div>
              </div>
              <div className="text-center">
                <div className="text-5xl font-bold text-blue-300 mb-2">10</div>
                <div className="text-sm text-blue-100 font-semibold">Patent Filings</div>
              </div>
              <div className="text-center">
                <div className="text-5xl font-bold text-cyan-300 mb-2">Defense</div>
                <div className="text-sm text-blue-100 font-semibold">Critical Applications</div>
              </div>
              <div className="text-center">
                <div className="text-5xl font-bold text-blue-300 mb-2">Trusted</div>
                <div className="text-sm text-blue-100 font-semibold">Electronics Assurance</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Defense Technology Showcase - Chips & Supply Chain Imagery */}
      <section className="py-16 bg-gradient-to-b from-blue-950/50 to-blue-900/30 border-y border-blue-500/20 relative overflow-hidden">
        {/* Floating R&D Equipment Images with 3D Animation */}
        <div className="absolute top-12 left-8 z-0 opacity-45" style={{ animation: 'float3d 15s ease-in-out infinite' }}>
          <img 
            src="https://images.unsplash.com/photo-1650530415027-dc9199f473ec?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHw0fHxlbGVjdHJvbmljcyUyMGVuZ2luZWVyaW5nJTIwcmVzZWFyY2glMjBsYWIlMjBlcXVpcG1lbnQlMjB0ZXN0aW5nfGVufDB8fHx8MTc3NjM3Mjg1MHww&ixlib=rb-4.1.0&q=85"
            alt="Electronic Testing Equipment"
            className="w-52 h-52 rounded-2xl border-4 border-cyan-400/30 shadow-2xl object-cover"
            style={{ transform: 'perspective(1000px) rotateY(18deg) rotateX(-12deg)' }}
          />
        </div>
        
        <div className="absolute bottom-20 right-16 z-0 opacity-50" style={{ animation: 'float3d 12s ease-in-out infinite', animationDelay: '2.5s' }}>
          <img 
            src="https://images.unsplash.com/photo-1766297247287-9bf80d5f8281?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwyfHxyZXNlYXJjaCUyMGRldmVsb3BtZW50JTIwbGFib3JhdG9yeSUyMHRlY2hub2xvZ3klMjBzY2llbnRpc3RzfGVufDB8fHx8MTc3NjM3Mjg1MHww&ixlib=rb-4.1.0&q=85"
            alt="Lab Research"
            className="w-60 h-60 rounded-2xl border-4 border-blue-400/30 shadow-2xl object-cover"
            style={{ transform: 'perspective(1000px) rotateY(-22deg) rotateX(8deg)' }}
          />
        </div>
        
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-white mb-4 flex items-center justify-center">
                <Cpu className="mr-3 text-cyan-400" size={40} />
                Defense R&D Engineering for Strategic Electronics
              </h2>
              <p className="text-xl text-blue-100">
                Supply chain resilience, microelectronics assurance, provenance-confidence &amp; counterfeit-avoidance
                workflow support, critical materials analysis, and manufacturing stability for defense and national
                security applications
              </p>
            </div>
            
            {/* Defense R&D Gallery - NO BRANDING */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-12">
              <div className="group relative aspect-square rounded-2xl overflow-hidden border-4 border-blue-500/40 shadow-2xl transform transition-all hover:scale-105 hover:shadow-cyan-500/50">
                <img 
                  src="https://images.unsplash.com/photo-1693932038683-7c35401f5307" 
                  alt="Semiconductor Manufacturing Cleanroom" 
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-blue-900/90 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                  <div className="absolute bottom-4 left-4 text-white font-semibold text-sm">
                    Manufacturing Stability
                  </div>
                </div>
              </div>
              
              <div className="group relative aspect-square rounded-2xl overflow-hidden border-4 border-cyan-500/40 shadow-2xl transform transition-all hover:scale-105 hover:shadow-blue-500/50">
                <img 
                  src="https://images.unsplash.com/photo-1494412651409-8963ce7935a7" 
                  alt="Global Supply Chain Resilience" 
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-cyan-900/90 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                  <div className="absolute bottom-4 left-4 text-white font-semibold text-sm">
                    Supply Chain Intelligence
                  </div>
                </div>
              </div>
              
              <div className="group relative aspect-square rounded-2xl overflow-hidden border-4 border-blue-500/40 shadow-2xl transform transition-all hover:scale-105 hover:shadow-cyan-500/50">
                <img 
                  src="https://images.unsplash.com/photo-1611250188473-5501e1c48785" 
                  alt="Defense Systems Engineering" 
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-blue-900/90 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                  <div className="absolute bottom-4 left-4 text-white font-semibold text-sm">
                    Defense Systems
                  </div>
                </div>
              </div>
              
              <div className="group relative aspect-square rounded-2xl overflow-hidden border-4 border-cyan-500/40 shadow-2xl transform transition-all hover:scale-105 hover:shadow-blue-500/50">
                <img 
                  src="https://images.unsplash.com/photo-1585206031650-9e9a7c87dcfe" 
                  alt="Provenance Evidence Review" 
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-cyan-900/90 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                  <div className="absolute bottom-4 left-4 text-white font-semibold text-sm">
                    Provenance &amp; Source-Risk
                  </div>
                </div>
              </div>
            </div>

            {/* Second Row - Strategic Materials & Global Network */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-12">
              <div className="group relative aspect-square rounded-2xl overflow-hidden border-4 border-blue-500/40 shadow-2xl transform transition-all hover:scale-105">
                <img 
                  src="https://images.unsplash.com/photo-1652294576306-e46550895c61" 
                  alt="Rare Earth Minerals & Critical Materials" 
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-blue-900/90 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                  <div className="absolute bottom-4 left-4 text-white font-semibold text-sm">
                    Critical Materials
                  </div>
                </div>
              </div>
              
              <div className="group relative aspect-square rounded-2xl overflow-hidden border-4 border-cyan-500/40 shadow-2xl transform transition-all hover:scale-105">
                <img 
                  src="https://images.unsplash.com/photo-1733195296321-b99d129b09cd" 
                  alt="Global Network Connectivity" 
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-cyan-900/90 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                  <div className="absolute bottom-4 left-4 text-white font-semibold text-sm">
                    Global Connectivity
                  </div>
                </div>
              </div>
              
              <div className="group relative aspect-square rounded-2xl overflow-hidden border-4 border-blue-500/40 shadow-2xl transform transition-all hover:scale-105">
                <img 
                  src="https://images.unsplash.com/photo-1532186773960-85649e5cb70b" 
                  alt="Advanced Manufacturing Facilities" 
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-blue-900/90 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                  <div className="absolute bottom-4 left-4 text-white font-semibold text-sm">
                    Advanced Manufacturing
                  </div>
                </div>
              </div>
              
              <div className="group relative aspect-square rounded-2xl overflow-hidden border-4 border-cyan-500/40 shadow-2xl transform transition-all hover:scale-105">
                <img 
                  src="https://images.unsplash.com/photo-1769335737970-2b6cbcff3a9f" 
                  alt="Defense Logistics & Operations" 
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-cyan-900/90 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                  <div className="absolute bottom-4 left-4 text-white font-semibold text-sm">
                    Defense Logistics
                  </div>
                </div>
              </div>
            </div>

            {/* American Grit Statement */}
            <div className="text-center bg-gradient-to-r from-blue-900/50 to-cyan-900/50 backdrop-blur-sm rounded-2xl p-8 border border-blue-400/30">
              <h3 className="text-3xl font-bold text-white mb-4">Rigorous Engineering Interpretation</h3>
              <p className="text-lg text-blue-100 leading-relaxed max-w-4xl mx-auto">
                SALAR is built for customers and partners who need <strong>rigorous engineering interpretation</strong> rather than generic consulting language: 
                design-rule discipline, failure-risk understanding, component and subsystem evaluation, source-confidence assessment, 
                process-control awareness, and technically defensible decision support across development, qualification, production, and sustainment environments.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Company Credibility & Stats */}
      <section className="py-20 bg-blue-950/30 backdrop-blur-sm border-y border-blue-500/20">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-4xl font-bold text-white mb-12 text-center">
              <Shield className="inline-block mr-3 text-cyan-400" size={40} />
              U.S.-Based Engineering Research Organization
            </h2>
            
            {/* Key Stats Grid - Enhanced */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-6 mb-16">
              <div className="text-center p-6 bg-gradient-to-br from-blue-600/30 to-cyan-600/30 rounded-xl border-2 border-blue-400/30 backdrop-blur-sm transform hover:scale-105 transition-all shadow-xl">
                <div className="text-5xl font-bold text-white mb-2">10</div>
                <div className="text-sm text-blue-100 font-semibold">Patent Filings</div>
              </div>
              <div className="text-center p-6 bg-gradient-to-br from-cyan-600/30 to-blue-600/30 rounded-xl border-2 border-cyan-400/30 backdrop-blur-sm transform hover:scale-105 transition-all shadow-xl">
                <div className="text-5xl font-bold text-white mb-2">5</div>
                <div className="text-sm text-cyan-100 font-semibold">Core Capabilities</div>
              </div>
              <div className="text-center p-6 bg-gradient-to-br from-blue-600/30 to-cyan-600/30 rounded-xl border-2 border-blue-400/30 backdrop-blur-sm transform hover:scale-105 transition-all shadow-xl">
                <div className="text-5xl font-bold text-white mb-2">13+</div>
                <div className="text-sm text-blue-100 font-semibold">Global Registrations</div>
              </div>
              <div className="text-center p-6 bg-gradient-to-br from-cyan-600/30 to-blue-600/30 rounded-xl border-2 border-cyan-400/30 backdrop-blur-sm transform hover:scale-105 transition-all shadow-xl">
                <div className="text-5xl font-bold text-white mb-2">4</div>
                <div className="text-sm text-cyan-100 font-semibold">NAICS Codes</div>
              </div>
              <div className="text-center p-6 bg-gradient-to-br from-blue-600/30 to-cyan-600/30 rounded-xl border-2 border-blue-400/30 backdrop-blur-sm transform hover:scale-105 transition-all shadow-xl">
                <div className="text-5xl font-bold text-white mb-2">2027</div>
                <div className="text-sm text-blue-100 font-semibold">SAM Active Until</div>
              </div>
            </div>

            {/* Company Registration & Contact Info - Enhanced Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <Card className="border-2 border-blue-400/40 bg-gradient-to-br from-blue-900/40 to-cyan-900/40 backdrop-blur-sm shadow-2xl hover:shadow-cyan-500/30 transition-all">
                <CardHeader>
                  <CardTitle className="text-white flex items-center text-2xl">
                    <FileText className="mr-3 text-cyan-400" size={32} />
                    Official Registration
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-blue-100 space-y-3 text-lg">
                  <p><strong className="text-cyan-300">Entity:</strong> Oregon Limited Liability Company</p>
                  <p><strong className="text-cyan-300">Registry No.:</strong> 254232599</p>
                  <p><strong className="text-cyan-300">Location:</strong> Wilsonville, Oregon, USA</p>
                </CardContent>
              </Card>

              <Card className="border-2 border-cyan-400/40 bg-gradient-to-br from-cyan-900/40 to-blue-900/40 backdrop-blur-sm shadow-2xl hover:shadow-blue-500/30 transition-all">
                <CardHeader>
                  <CardTitle className="text-white flex items-center text-2xl">
                    <Shield className="mr-3 text-blue-400" size={32} />
                    Contact Information
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-blue-100 space-y-4">
                  <div className="flex items-center space-x-3 text-lg">
                    <Mail size={24} className="text-cyan-300 flex-shrink-0" />
                    <a href="mailto:contact@salartechnologies.com" className="hover:text-white transition-colors font-semibold">
                      contact@salartechnologies.com
                    </a>
                  </div>
                  <div className="flex items-center space-x-3 text-lg">
                    <Mail size={24} className="text-cyan-300 flex-shrink-0" />
                    <a href="mailto:vikrant@salartechnologies.com" className="hover:text-white transition-colors font-semibold">
                      vikrant@salartechnologies.com
                    </a>
                  </div>
                  <div className="flex items-center space-x-3 text-lg">
                    <Phone size={24} className="text-cyan-300 flex-shrink-0" />
                    <a href="tel:408-601-7755" className="hover:text-white transition-colors font-semibold">
                      408-601-7755
                    </a>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Why This Matters */}
      <section className="py-16">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-white mb-8 text-center">Why This Matters</h2>
            <Card className="border-2 border-blue-500/40 bg-blue-900/30 backdrop-blur-sm shadow-2xl">
              <CardContent className="pt-8">
                <p className="text-xl text-blue-100 leading-relaxed">
                  <strong>A board can be electrically functional and still be fragile in the field.</strong> A part can match a datasheet and still carry pedigree or handling risk. 
                  A factory can appear available on paper while process instability undermines trusted output. A sourcing path can look diversified at tier one while hiding 
                  shared lower-tier chokepoints. SALAR addresses those realities by organizing engineering evidence into one integrated technical picture.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Capability Preview */}
      <section className="py-20 bg-gradient-to-b from-blue-950/30 to-blue-900/20">
        <div className="container mx-auto px-6">
          <h2 className="text-4xl font-bold text-white mb-4 text-center">Core Capabilities</h2>
          <p className="text-xl text-blue-200 mb-12 text-center">Engineering-centered solutions for mission-critical systems</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {capabilities.map((capability) => (
              <Card key={capability.id} className="border-2 border-blue-500/40 bg-gradient-to-br from-blue-900/40 to-cyan-900/30 backdrop-blur-sm hover:border-cyan-400/60 transition-all hover:shadow-2xl hover:shadow-cyan-500/20 transform hover:scale-105">
                <CardHeader>
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-cyan-600 rounded-xl flex items-center justify-center mb-4 shadow-lg">
                    <CheckCircle className="text-white" size={32} />
                  </div>
                  <CardTitle className="text-white text-xl">{capability.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-blue-100 text-base">
                    {capability.description}
                  </CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="text-center mt-16">
            <Link to="/capabilities">
              <Button size="lg" className="bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white text-lg px-10 py-6 shadow-2xl">
                View All Capabilities
                <ArrowRight className="ml-3" size={24} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Government/Partnership Relevance */}
      <section className="py-20 bg-gradient-to-r from-blue-900/50 to-cyan-900/50 backdrop-blur-sm text-white border-y border-blue-400/30">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto text-center">
            <Shield className="w-20 h-20 mx-auto mb-6 text-cyan-300 drop-shadow-2xl" />
            <h2 className="text-4xl font-bold mb-6">Government & Partnership Relevance</h2>
            <p className="text-xl text-blue-100 mb-10 leading-relaxed">
              SALAR is positioned for defense-adjacent, strategic-industry technical engagement in areas 
              including electronics reliability, supply-chain resilience, provenance assurance, and 
              manufacturing stability. Registered across <strong>13+ international procurement platforms</strong>.
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <Link to="/government-defense">
                <Button size="lg" className="bg-white text-blue-900 hover:bg-blue-100 text-lg px-10 py-6 shadow-2xl font-bold">
                  View All Credentials
                  <ArrowRight className="ml-3" size={24} />
                </Button>
              </Link>
              <Link to="/publications">
                <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-blue-900 text-lg px-10 py-6 shadow-2xl font-bold">
                  Research Publications
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Documents */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <h2 className="text-4xl font-bold text-white mb-4 text-center">Featured Documents</h2>
          <p className="text-xl text-blue-200 mb-12 text-center">Public-safe technical resources and capability statements</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {documents.slice(0, 3).map((doc) => (
              <Card key={doc.id} className="border-2 border-blue-500/40 bg-gradient-to-br from-blue-900/40 to-cyan-900/30 backdrop-blur-sm hover:border-cyan-400/60 transition-all hover:shadow-2xl hover:shadow-blue-500/20">
                <CardHeader>
                  <div className="flex items-start justify-between mb-4">
                    <FileText className="text-cyan-400" size={40} />
                    <span className="text-xs bg-blue-600 px-3 py-1 rounded-full text-white font-semibold">
                      {doc.fileType}
                    </span>
                  </div>
                  <CardTitle className="text-white text-xl">{doc.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-blue-100 mb-6 text-base">
                    {doc.description}
                  </CardDescription>
                  <Button 
                    onClick={() => window.open(doc.url, '_blank')}
                    className="w-full bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white font-semibold"
                  >
                    Download
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="text-center mt-12">
            <Link to="/documents">
              <Button size="lg" variant="outline" className="border-2 border-blue-400 text-blue-100 hover:bg-blue-600 hover:text-white text-lg px-10 py-6 shadow-xl">
                View All Resources
                <ArrowRight className="ml-3" size={24} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Mission — Why SALAR Matters */}
      <section
        className="py-20"
        style={{ background: '#0F1A2B', borderTop: '1px solid rgba(96,165,250,0.15)' }}
        data-testid="home-mission-section"
      >
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <div className="text-cyan-300 text-xs font-bold tracking-widest uppercase mb-4">
                Why SALAR Matters
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-5 leading-tight">
                Engineering continuity for mission-critical electronics.
              </h2>
              <p className="text-base md:text-lg text-blue-100 leading-relaxed">
                SALAR&rsquo;s mission is to help convert fragmented design, sourcing, provenance, and
                manufacturing signals into disciplined engineering decisions&mdash;before fragile
                electronics become mission risk.
              </p>
            </div>
            <div className="order-1 lg:order-2">
              <div
                className="rounded-xl overflow-hidden border border-cyan-400/25 mx-auto"
                style={{ maxWidth: 520, boxShadow: '0 10px 40px rgba(0,0,0,0.4)' }}
              >
                <img
                  src="/img/salar_mission_continuity.webp"
                  alt="Abstract protected mission-core circuit visual representing continuity, reliability, and trusted engineering."
                  loading="lazy"
                  className="block w-full h-auto"
                  data-testid="home-mission-image"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section
        className="py-20 text-white relative"
        style={{
          backgroundImage: "linear-gradient(rgba(10,22,40,0.85), rgba(10,22,40,0.95)), url('/img/data_center_servers.jpg')",
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
        data-testid="home-cta"
      >
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto text-center">
            <h2 className="text-4xl font-bold mb-6">Ready to Discuss Your Requirements?</h2>
            <p className="text-xl mb-10 text-blue-100">
              Contact SALAR Technologies LLC for government, defense, partnership, or technical collaboration inquiries.
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <Link to="/contact">
                <Button size="lg" className="bg-cyan-600 hover:bg-cyan-700 text-white text-lg px-10 py-6 shadow-2xl font-bold">
                  <Mail className="mr-3" size={24} />
                  Start a Conversation
                </Button>
              </Link>
              <Link to="/documents">
                <Button size="lg" variant="outline" className="border-2 border-cyan-300 text-cyan-200 hover:bg-cyan-300 hover:text-[#0a1628] text-lg px-10 py-6 shadow-2xl font-bold">
                  Download Resources
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
